import { ComponentRef } from '@angular/core';
import { IDataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import {
  EventHandlerPayloadResolveMoment,
  IDynamicEventHandler,
  IDynamicItem,
  isIDynamicBindableValue,
  isIDynamicBindableValueCombination,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  ComponentsRelationshipService,
  resolveValues,
} from '@epicuro-next/platform/dynamic-value-resolving';
import { ensureArray } from '@epicuro-next/utilities';
import { Action } from '@ngrx/store';
import { IdProviderService } from 'libs/platform/dynamic-value-resolving/src/lib/services/id-provider.service';
import {
  firstValueFrom,
  isObservable,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

import { IComponentOptions } from '../services/components-resolver.service';

import { dispatchNgRxAction } from './ngrx-action-utils';
import { dispatchDynamicEvent } from '@epicuro-next/platform/dynamic-actions';

export function setupAllDifferentKindsOfEventHandlers(
  eventHandlers: IComponentOptions['eventHandlers'],
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  dispatchAction: (action: Action) => void,
): void {
  if (!eventHandlers) {
    return;
  }

  const nativeElement = componentRef.location.nativeElement as HTMLElement;

  const eventHandlerSubscriberMap = getEventHandlerSubscriberMap(
    nativeElement,
    eventHandlers,
  );

  for (const eventHandlerSet of eventHandlerSubscriberMap) {
    if (eventHandlerSet.eventHandlers !== undefined) {
      const handlers = ensureArray(eventHandlerSet.eventHandlers).map(
        (handler) => {
          return handler.payloadFetchMoment ===
            EventHandlerPayloadResolveMoment.BeforeAddEventListener
            ? {
                ...handler,
                payload$: resolvePayloadBeforeAddEventListener(
                  handler,
                  dataProvidersResolver,
                  componentsRelationshipService,
                  idProviderService,
                  componentRef,
                ),
              }
            : handler;
        },
      );

      eventHandlerSet.eventSubscriber(handlers, async (eventHandlersArg) => {
        await executeEventHandlers(
          eventHandlersArg,
          dataProvidersResolver,
          componentsRelationshipService,
          idProviderService,
          componentRef,
          dispatchAction,
        ).catch((error) => {
          throw error;
        });
      });
    }
  }
}

export function setupOutputEventHandlers(
  eventHandlers: IComponentOptions['outputs'],
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  dispatchAction: (action: Action) => void,
): void {
  if (!eventHandlers) {
    return;
  }

  const componentInstance = componentRef.instance as {
    [key in string]: Observable<unknown>;
  };

  const componentDestroyed$ = new Subject<void>();
  componentRef.onDestroy(() => {
    componentDestroyed$.next();
  });

  for (const [key, eventHandlerOrArray] of Object.entries(eventHandlers)) {
    const arrayOfEventHandlers = ensureArray(eventHandlerOrArray);
    componentInstance[key]
      .pipe(takeUntil(componentDestroyed$))
      .subscribe(async (eventData) => {
        await executeEventHandlers(
          arrayOfEventHandlers,
          dataProvidersResolver,
          componentsRelationshipService,
          idProviderService,
          componentRef,
          dispatchAction,
          { eventData },
        ).catch((error) => {
          throw error;
        });
      });
  }
}

async function executeEventHandlers(
  eventHandlersToExecute: IDynamicEventHandler | IDynamicEventHandler[],
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
  dispatchAction: (action: Action) => void,
  eventData?: { eventData?: unknown },
): Promise<void> {
  const onEventHandlers = ensureArray(eventHandlersToExecute);

  const eventHandlersExt: {
    eventHandler: IDynamicEventHandler;
    payload: Observable<unknown> | (() => Observable<unknown>);
  }[] = await preresolvePayloadForEventHandlerIfNeeded(
    onEventHandlers,
    componentRef,
    dataProvidersResolver,
    componentsRelationshipService,
    idProviderService,
    eventData,
  );

  for (const { eventHandler, payload } of eventHandlersExt) {
    handleEvent(eventHandler, dispatchAction, payload);
  }
}

async function resolvePayloadBeforeAddEventListener(
  eventHandlersToExecute: IDynamicEventHandler,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  componentRef: ComponentRef<unknown>,
) {
  const payload$ = getPayload(
    componentRef,
    dataProvidersResolver,
    componentsRelationshipService,
    idProviderService,
    eventHandlersToExecute,
  );

  const componentDestroyed$ = new Subject<void>();
  componentRef.onDestroy(() => {
    componentDestroyed$.next();
  });

  return await firstValueFrom(payload$.pipe(takeUntil(componentDestroyed$)));
}

function getEventHandlerSubscriberMap(
  nativeElement: HTMLElement,
  eventHandlers: Exclude<IDynamicItem['eventHandlers'], undefined>,
) {
  type EventSubscriberType = (
    eventHandlers: IDynamicEventHandler | IDynamicEventHandler[],
    callback: (
      eventHandlersCallbackArg: IDynamicEventHandler | IDynamicEventHandler[],
    ) => Promise<void>,
  ) => void;

  const onClickEventSubscriber: EventSubscriberType = (
    eventHandlersArgs,
    callback,
  ) => {
    nativeElement.addEventListener('click', async (event) => {
      await callback(eventHandlersArgs);
      event.stopPropagation();
    });
  };

  const onVisibleOnceEventSubscriber: EventSubscriberType = (
    eventHandlersArgs,
    callback,
  ) => {
    onVisibleOnce(nativeElement, async () => {
      await callback(eventHandlersArgs);
    });
  };

  const eventHandlerSubscriberMap: {
    eventHandlers?: IDynamicEventHandler | IDynamicEventHandler[];
    eventSubscriber: EventSubscriberType;
  }[] = [
    {
      eventHandlers: eventHandlers.onClick,
      eventSubscriber: onClickEventSubscriber,
    },
    {
      eventHandlers: eventHandlers.onVisibleOnce,
      eventSubscriber: onVisibleOnceEventSubscriber,
    },
  ];

  return eventHandlerSubscriberMap;
}

async function preresolvePayloadForEventHandlerIfNeeded(
  onEventHandlers: IDynamicEventHandler[],
  componentRef: ComponentRef<unknown>,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  eventData?: { eventData?: unknown },
) {
  type PayloadType = Observable<unknown>;

  const eventHandlersExt: {
    eventHandler: IDynamicEventHandler;
    payload: PayloadType | (() => PayloadType);
  }[] = [];

  for (const onEventHandler of onEventHandlers) {
    let payload: PayloadType | (() => PayloadType) | undefined;

    if (onEventHandler.payloadFetchMoment === undefined) {
      onEventHandler.payloadFetchMoment =
        EventHandlerPayloadResolveMoment.AtEvent;
    }

    const getPayloadFunc = () =>
      getPayload(
        componentRef,
        dataProvidersResolver,
        componentsRelationshipService,
        idProviderService,
        onEventHandler,
        eventData,
      );

    payload = await applyPayloadResolveMomentStrategy(
      onEventHandler,
      getPayloadFunc,
    );

    eventHandlersExt.push({
      eventHandler: onEventHandler,
      payload,
    });
  }

  return eventHandlersExt;
}

async function applyPayloadResolveMomentStrategy(
  onEventHandler: IDynamicEventHandler,
  getPayloadFunc: () => Observable<unknown>,
): Promise<Observable<unknown> | (() => Observable<unknown>)> {
  switch (onEventHandler.payloadFetchMoment) {
    case EventHandlerPayloadResolveMoment.AtEvent:
      return getPayloadFunc;
    case EventHandlerPayloadResolveMoment.BeforeAddEventListener:
      return of(await firstValueFrom(getPayloadFunc()));
    case EventHandlerPayloadResolveMoment.AtEventAndBeforeAnyEventHandlersAreExecuted:
      return of(await firstValueFrom(getPayloadFunc()));
    default:
      throw new Error(
        `There is no implementatation for payload resolve moment '${onEventHandler.payloadFetchMoment}'.`,
      );
  }
}

export function handleEvent(
  eventHandler: IDynamicEventHandler,
  dispatchAction: (action: Action) => void,
  payload: Observable<unknown> | (() => Observable<unknown>),
): void {
  let payload$: Observable<unknown>;
  if (typeof payload === 'function') {
    payload$ = payload();
  } else {
    payload$ = payload;
  }

  if (eventHandler.type === 'ngrx') {
    dispatchNgRxAction(eventHandler, payload$, dispatchAction);

    return;
  }

  if (eventHandler.type === 'custom-event') {
    payload$.pipe(take(1)).subscribe((payload) => {
      dispatchDynamicEvent(eventHandler.action, payload);
    });

    return;
  }

  console.warn(
    `Handler for event of type '${eventHandler.type}' is not implemented.`,
  );
}

export function getPayload(
  componentRef: ComponentRef<unknown>,
  dataProvidersResolver: IDataProvidersResolver,
  componentsRelationshipService: ComponentsRelationshipService,
  idProviderService: IdProviderService,
  eventHandler: { payload$?: unknown },
  eventData?: { eventData?: unknown },
): Observable<unknown> {
  const payload$ = eventHandler.payload$;

  const payloadIsIDynamicBindableValue = isIDynamicBindableValue(payload$);
  const payloadIsIDynamicBindableValueCombination =
    isIDynamicBindableValueCombination(payload$);

  if (
    !payloadIsIDynamicBindableValue &&
    !payloadIsIDynamicBindableValueCombination
  ) {
    if (!isObservable(payload$)) {
      return of(payload$);
    }
    return payload$;
  }

  if (eventData !== undefined) {
    dataProvidersResolver.setOneTimeValue('event', eventData.eventData);
  }

  const resolvedValues = resolveValues(
    { payload$ },
    dataProvidersResolver,
    componentsRelationshipService,
    idProviderService,
    componentRef,
  );

  let resolvedPayload$: Observable<unknown>;
  if (!isObservable(resolvedValues.payload$)) {
    resolvedPayload$ = of(resolvedValues.payload$);
  } else {
    resolvedPayload$ = resolvedValues.payload$;
  }

  return resolvedPayload$;
}

function onVisibleOnce(
  element: HTMLElement,
  callback: () => Promise<void>,
): void {
  new IntersectionObserver(async (entries, observer) => {
    for (const entry of entries) {
      if (entry.intersectionRatio > 0) {
        await callback();
        observer.disconnect();
      }
    }
  }).observe(element);
}
